import type { Root } from 'mdast';
import type { LeafDirective } from 'mdast-util-directive';
import { visit } from 'unist-util-visit';

const CMS_COMPONENT_DIRECTIVE = 'component';

/**
 * Maps `::component{type="…" …}` leaf directives to HAST via mdast `data.hName` /
 * `data.hProperties` (see remark-directive readme). Unrecognized or invalid
 * directives are left untouched and emit nothing.
 */
export function remarkCmsComponentDirective() {
    return (tree: Root) => {
        visit(tree, 'leafDirective', (node) => {
            const directive = node as LeafDirective;
            if (directive.name !== CMS_COMPONENT_DIRECTIVE) {
                return;
            }

            const attributes = directive.attributes ?? {};
            const componentType = attributes.type?.trim();
            if (!componentType) {
                return;
            }

            const { type: _type, ...restAttributes } = attributes;
            const hProperties: Record<string, string> = {
                'data-cms-type': componentType,
            };

            for (const [key, value] of Object.entries(restAttributes)) {
                if (typeof value !== 'string' || value === '') {
                    continue;
                }
                hProperties[key] = value;
            }

            const data = directive.data ?? (directive.data = {});
            data.hName = 'cms-component';
            data.hProperties = hProperties;
            data.hChildren = [];
        });
    };
}

/** Short authoring reference for Strapi / internal docs (not rendered). */
export const CMS_COMPONENT_MARKDOWN_CHEAT_SHEET = `
CMS embedded components (leaf directives)

Video — YouTube watch or youtu.be URL only:
::component{type="Video" url="https://www.youtube.com/watch?v=dQw4w9WgXcQ"}

Cloudflare Stream (Stream UID only, not a full URL). Use orientation="portrait" for 9:16 (vertical) video:
::component{type="CloudflareStream" uid="<stream-uid>" orientation="portrait"}

Landscape (default):
::component{type="CloudflareStream" uid="<stream-uid>"}

Callout:
::component{type="Callout" text="Important note for readers." tone="info"}
::component{type="Callout" text="Warning text" tone="warning"}

Instagram — public post or reel URL only:
::component{type="Instagram" url="https://www.instagram.com/p/SHORTCODE/"}
::component{type="Instagram" url="https://www.instagram.com/reel/SHORTCODE/"}

X (Twitter) — public status URL (x.com or twitter.com):
::component{type="XPost" url="https://x.com/username/status/1234567890"}

Rules:
- Use double colons + the word component + attributes in braces.
- Use only the attributes documented for each type; values must be double-quoted when they contain spaces.
`.trim();
